import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLiveSelectedShapes } from '@/hooks/use-live-selected-shapes'
import {
  effectSyncToken,
  formatEffectInputText,
  resolveEffectSelection,
} from '@/lib/object-effects/effect-selection'
import type { EffectControlProps } from '../types/effect-control-props'
import type { EffectSliderBindings } from '../types/effect-slider-bindings'
import type { EffectPreset, GeometryEffect } from '../types/geometry-effect'
import { useDeferredCommitInput } from './use-deferred-commit-input'
import { useGeometryEffectDocument } from './use-geometry-effect-document'
import { useSelectionSnapshotGuard } from './use-selection-snapshot-guard'

function matchingPreset(presets: readonly EffectPreset[], n: number): string | null {
  return presets.find((o) => o.value === n)?.label ?? null
}

/**
 * Field state machine shared by the numeric geometry effects (Rounding, Sides, Inset). Slider +
 * presets update live and push undo on release; the text field only commits and validates on
 * Enter/Tab/blur, with an inline range error + red border for invalid input (invalid text kept),
 * silent restore for an empty commit, and the controls disabled when nothing eligible is selected.
 *
 * Multi-selection rules (via `lib/object-effects/effect-selection`): enabled when any selected
 * object is eligible, a commit applies to every eligible object and passes over the rest, and the
 * field is blank when eligible objects hold different values.
 */
export function useGeometryEffectControl(
  effect: GeometryEffect,
  {
    disabled = false,
    shapeManagerRef,
    onShapeCommitted,
    onBatchShapesCommitted,
  }: EffectControlProps
): EffectSliderBindings {
  const selectedShapes = useLiveSelectedShapes(shapeManagerRef)
  const [sliderValue, setSliderValue] = useState([effect.restValue])
  const [inputText, setInputText] = useState(String(effect.restValue))
  const [error, setError] = useState<string | undefined>(undefined)
  const [selectPreset, setSelectPreset] = useState<string | null>(null)
  /**
   * True while the blank field represents "eligible objects disagree" rather than a value the user
   * cleared. Committing an empty field must leave a mixed placeholder blank instead of restoring a
   * number, which would claim every eligible object shares it.
   */
  const [isMixedValue, setIsMixedValue] = useState(false)

  const selection = useMemo(
    () =>
      resolveEffectSelection(selectedShapes, {
        isEligible: effect.isEligible,
        readValue: effect.readValue,
        fallbackValue: effect.restValue,
        liveShapes: shapeManagerRef?.current?.getShapes(),
      }),
    [effect, selectedShapes, shapeManagerRef]
  )
  const { eligible, ineligible } = selection
  const controlDisabled = disabled || !selection.hasEligible

  /**
   * Committed value in the document + selection. Does not change from partial text input (model
   * updates only on commit), so the sync effect will not clobber the field while typing.
   */
  const syncToken = useMemo(
    () =>
      effectSyncToken(selectedShapes, {
        isEligible: effect.isEligible,
        readValue: effect.readValue,
        liveShapes: shapeManagerRef?.current?.getShapes(),
      }),
    [effect, selectedShapes, shapeManagerRef]
  )

  const { beginSession, snapshotStarts, clearStarts, clearSession, applyLive, commitWithHistory } =
    useGeometryEffectDocument({
      effect,
      shapeManagerRef,
      eligible,
      selected: selection.selected,
      onShapeCommitted,
      onBatchShapesCommitted,
    })
  const { captureSelectionSnapshot, selectionChangedSinceSnapshot } =
    useSelectionSnapshotGuard(shapeManagerRef)

  /* eslint-disable react-hooks/exhaustive-deps -- Only `syncToken` in deps. Including the
   * eligible shapes re-runs every parent render and clobbers partial text input; they are read
   * from the same render that produced the token. */
  useEffect(() => {
    if (!selection.hasEligible) {
      setSliderValue([effect.restValue])
      setInputText(String(effect.restValue))
      setSelectPreset(null)
      setIsMixedValue(false)
      return
    }

    const { commonValue, sliderValue: nextSliderValue } = effect.readCommonValue
      ? effect.readCommonValue(eligible, selectedShapes)
      : selection

    setSliderValue([nextSliderValue])
    setInputText(formatEffectInputText(commonValue))
    setError(undefined)
    setSelectPreset(commonValue == null ? null : matchingPreset(effect.presets, commonValue))
    setIsMixedValue(commonValue == null)
  }, [syncToken])
  /* eslint-enable react-hooks/exhaustive-deps */

  /**
   * The committed state to fall back on: `value` is null when eligible objects disagree, in which
   * case the field reverts to blank rather than to any one object's value.
   */
  const readCommitted = useCallback((): { value: number | null; sliderValue: number } => {
    const liveShapes = shapeManagerRef?.current?.getShapes()
    const resolved = effect.readCommonValue
      ? effect.readCommonValue(eligible, liveShapes ?? selectedShapes)
      : resolveEffectSelection(eligible, {
          isEligible: effect.isEligible,
          readValue: effect.readValue,
          fallbackValue: effect.restValue,
          liveShapes,
        })

    return { value: resolved.commonValue, sliderValue: resolved.sliderValue }
  }, [effect, eligible, selectedShapes, shapeManagerRef])

  /** Restore the field, slider, and preset to the committed state, clearing any error. */
  const revertToCommitted = useCallback(() => {
    const committed = readCommitted()

    setSliderValue([committed.sliderValue])
    setInputText(formatEffectInputText(committed.value))
    setSelectPreset(
      committed.value == null ? null : matchingPreset(effect.presets, committed.value)
    )
    setIsMixedValue(committed.value == null)
    setError(undefined)
  }, [effect, readCommitted])

  /**
   * `reason` distinguishes an Enter commit (invalid input stays visible with an error, per
   * AC7) from a blur commit — Tab, canvas click, or clicking elsewhere on the panel — where an
   * invalid value should instead silently revert with the error cleared (AC8).
   *
   * @returns true if committed or empty-restore was successful
   */
  const tryCommitInput = useCallback(
    (reason: 'enter' | 'blur' = 'enter'): boolean => {
      if (selectionChangedSinceSnapshot()) {
        // The canvas selection moved to different shapes while this edit was still uncommitted.
        // Discard the draft rather than committing it to the shapes that were selected when the
        // edit started — the field itself will resync to the new selection on the next render.
        setError(undefined)
        clearSession()
        return true
      }

      const raw = inputText.trim()

      if (raw === '') {
        // A blank mixed-selection placeholder stays blank — there is no single previous value to
        // restore, and writing one would misreport the other eligible objects.
        if (isMixedValue) {
          setError(undefined)
          return true
        }

        // silently restore last committed value, no error
        revertToCommitted()
        return true
      }

      const v = effect.parseInput(raw)

      if (v == null) {
        if (reason === 'blur') {
          // leaving the field: silently revert the displayed text too, and clear the error
          revertToCommitted()
        } else {
          // still focused (Enter): keep the invalid text visible with the error shown
          const committed = readCommitted()

          setSliderValue([committed.sliderValue])
          setSelectPreset(
            committed.value == null ? null : matchingPreset(effect.presets, committed.value)
          )
          setError(effect.rangeError)
        }

        return false
      }

      setError(undefined)
      snapshotStarts()
      setSliderValue([v])
      setInputText(String(v))
      setSelectPreset(matchingPreset(effect.presets, v))
      setIsMixedValue(false)
      applyLive(v)
      commitWithHistory(v)
      clearStarts()
      return true
    },
    [
      applyLive,
      clearSession,
      clearStarts,
      commitWithHistory,
      effect,
      inputText,
      isMixedValue,
      readCommitted,
      revertToCommitted,
      selectionChangedSinceSnapshot,
      snapshotStarts,
    ]
  )

  /** Esc: revert to the last committed value without applying or committing the draft. */
  const handleEscapeRevert = useCallback(() => {
    revertToCommitted()
    clearStarts()
  }, [clearStarts, revertToCommitted])

  const handleSliderChange = (value: number[]) => {
    setError(undefined)
    const n = value[0] ?? effect.restValue

    setSliderValue([n])
    setInputText(String(n))
    setSelectPreset(matchingPreset(effect.presets, n))
    setIsMixedValue(false)
    applyLive(n)
  }

  const handleSliderCommit = (value: number[]) => {
    const n = value[0] ?? effect.restValue

    commitWithHistory(n)
    clearStarts()
  }

  const handlePresetChange = (val: string) => {
    if (controlDisabled || ineligible) return
    const option = effect.presets.find((opt) => opt.label === val)

    if (option) {
      beginSession()
      setSliderValue([option.value])
      setInputText(String(option.value))
      setSelectPreset(val)
      setIsMixedValue(false)
      applyLive(option.value)
      commitWithHistory(option.value)
      clearStarts()
    }
  }

  const inputHandlers = useDeferredCommitInput({
    tryCommitInput,
    onEscape: handleEscapeRevert,
    onFocus: () => {
      snapshotStarts()
      captureSelectionSnapshot()
    },
    onDraftChange: (text) => {
      setError(undefined)
      // Once the user types, the field is their draft — not the mixed placeholder.
      setIsMixedValue(false)
      setInputText(text)
    },
  })

  return {
    presets: effect.presets,
    selectedPreset: selectPreset,
    onPresetChange: handlePresetChange,
    sliderValue,
    min: effect.min,
    max: effect.max,
    onSliderChange: handleSliderChange,
    onSliderCommit: handleSliderCommit,
    onSessionStart: beginSession,
    inputText,
    inputHandlers,
    error,
    disabled: controlDisabled || ineligible,
    dimmed: ineligible,
  }
}
